/**
 * Refreshes the conversation list while the inbox is on screen, raises an
 * in-app notice for the messages each refresh reveals, and keeps quiet for the
 * thread the member is already reading.
 *
 * The web build has no push channel or socket (`pushMessaging.web.ts` reports
 * no token by design), so the list request is the only new-message signal.
 * Polling is therefore scoped to the one route that renders the list live: the
 * inbox. A chat page is served by Chat.tsx's own 4s message poll, and the nav
 * unread badge is kept current by `useConversationBadgeSync`, which refetches
 * on the navigations that can change it rather than on a timer — so an interval
 * away from `/messages` would add traffic without changing what is on screen.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMessages } from '@wlb/shared/context/MessageContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { isAiConversationType } from '@wlb/shared/constants';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { Conversation } from '@wlb/shared/types/chat';
import {
  activeConversationIdFromPath,
  buildNoticeData,
  findNewIncomingMessages,
  latestMessageAt,
  shouldSuppressNotice,
  snapshotConversations,
  type ConversationSnapshot,
} from './incomingMessageNotice';

const POLL_INTERVAL_MS = 20000;

/** Route that renders the conversation list itself. */
const INBOX_PATH = '/messages';

/** A quiet minute can still deliver a burst; the badges carry the remainder. */
const MAX_NOTICES_PER_UPDATE = 3;

const isDocumentVisible = (): boolean => document.visibilityState === 'visible';

const isAiThread = (conversation: Conversation): boolean =>
  conversation.category === 'ai' || isAiConversationType(conversation.conversationType);

export function useIncomingMessageNotices(): void {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { conversations, listLoadedOnce, fetchConversations, syncActiveMessages } = useMessages();

  // The route is the authority on what the member is reading: the context's
  // activeConversationId outlives the Chat page, which would keep suppressing
  // notices for a thread that has already been left.
  const activeConversationId = useMemo(
    () => activeConversationIdFromPath(pathname),
    [pathname],
  );

  // Only the inbox route itself: `/messages/<id>`, its details page and
  // `/messages/group/new` do not render the list.
  const inboxOnScreen = pathname === INBOX_PATH;

  const snapshotRef = useRef<ConversationSnapshot | null>(null);
  const baselineTakenRef = useRef(false);
  const watermarkRef = useRef(0);
  const resyncPendingRef = useRef(false);

  useEffect(() => {
    // Off the inbox there is nothing to refresh for: the Messages page fetches
    // the list when it mounts, so entering the route needs no leading request
    // here either.
    if (!inboxOnScreen) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = (): void => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    // No search/filter arguments: the inbox owns those, and overriding them
    // would rewrite the slice the member is reading.
    const refresh = (): void => {
      fetchConversations({ refresh: true }).catch(() => undefined);
    };

    const start = (): void => {
      stop();
      timer = setInterval(refresh, POLL_INTERVAL_MS);
    };

    const onVisibilityChange = (): void => {
      if (!isDocumentVisible()) {
        stop();
        return;
      }
      // Messages that arrived while the tab was hidden are history by the time
      // the member is back — refresh the badges, announce nothing. Pinned to
      // the update the refresh produces rather than to a timer, so a slow
      // request cannot outrun the quiet period and dump the backlog as toasts.
      resyncPendingRef.current = true;
      refresh();
      start();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    if (isDocumentVisible()) start();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [inboxOnScreen, fetchConversations]);

  useEffect(() => {
    const previous = snapshotRef.current;
    snapshotRef.current = snapshotConversations(conversations);
    // Read before it is raised: the messages this update carries have to be
    // measured against what was known before them.
    const watermark = watermarkRef.current;
    // Monotonic — a narrower list must not lower the bar for what counts as new.
    watermarkRef.current = Math.max(watermark, latestMessageAt(conversations));

    // Nothing is news until the inbox has actually answered once. The context
    // seeds `conversations` with an empty list and a failed first fetch leaves
    // it empty too, so those placeholders are not a baseline: measured against
    // them, every pre-existing unread thread would toast on app start.
    const baselineTaken = baselineTakenRef.current;
    baselineTakenRef.current = listLoadedOnce;
    if (previous === null || !baselineTaken) return;

    // The resync that follows a tab regaining focus only updates the badges.
    if (resyncPendingRef.current) {
      resyncPendingRef.current = false;
      return;
    }

    // A background tab announces nothing: an in-app toast would expire unseen,
    // and the refreshed unread badges already carry the news on return. The
    // rows are folded into the snapshot above, so they stay quiet then too.
    if (!isDocumentVisible()) return;

    const incoming = findNewIncomingMessages(previous, conversations, { watermark }).filter(
      conversation => !isAiThread(conversation),
    );

    for (const conversation of incoming.slice(0, MAX_NOTICES_PER_UPDATE)) {
      const suppressed = shouldSuppressNotice({
        conversationId: String(conversation.id),
        activeConversationId,
      });

      if (suppressed) {
        // No notice, but do not make the reader wait for the thread's own poll.
        syncActiveMessages(String(conversation.id)).catch(() => undefined);
        continue;
      }

      ToastService.show({
        type: 'info',
        duration: 4000,
        ...buildNoticeData(conversation, t('chatNotifications.newMessage')),
      });
    }
    // `listLoadedOnce` is a dependency so the first answered list becomes the
    // baseline instead of the update after it.
  }, [conversations, listLoadedOnce, activeConversationId, syncActiveMessages, t]);
}
